import type {Suggest} from '../mod-multisearch/ISuggestions';
import {html, LitElement, type TemplateResult} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';

import styles from './autocomplete.styles';
import elements from '../../ts/css/elements.styles';
import keyFrames from '../../ts/css/keyframes.style';
import {resetStyles} from '../../ts/css/resets.styles';
import generalStyles from '../../ts/css/general.styles';
import {iconsFont} from '../../ts/css/icons.styles';

import viewport from '../../ts/services/viewport';
import Features from '../../ts/services/features';
import {Cookies, Logger, PubSub} from '@mamdev/cad.static-next-lib';
import {Url} from '../../ts/services/url';
import {live} from 'lit/directives/live.js';

type AutocompleteSuggest = Pick<Suggest, 'label' | 'url'>;

interface IAutocompleteResult {
  term: string;
  suggestions: AutocompleteSuggest[];
}

interface IGeoResponse {
  valid: boolean;
  cities: City[];
}

interface City {
  districtId: number;
  country: string;
  countryCode: string;
  county: string;
  encodedName: string;
  url: string;
  city: string;
  loc_weather: string;
}

@customElement('ui-autocomplete')
export class Autocomplete extends LitElement {
  static readonly styles = [resetStyles, keyFrames, generalStyles, iconsFont, elements, styles];

  @property() for: 'weather' | 'sement';
  @property() url = '';
  @property({attribute: 'autosuggest-url'}) autoSuggestUrl = '';
  @property({attribute: 'formaction'}) formAction = '/wetter/suchvorschlaege/';

  // specific to only weather autocomplete
  @property() locationservice = '';

  // general state
  @state() suggestions: AutocompleteSuggest[];
  @state() placeholder: string;
  @state() show = false;
  @state() selectedMenuIndex = -1;
  @state() searchTerm = '';

  // specific to only sement autocomplete
  @state() searchResultData: IAutocompleteResult;
  @state() showSearchResult = false;
  // specific to only weather autocomplete
  @state() isGeoSupported = true;
  @state() hasGeoError = false;
  @state() playPositionAnimation = false;

  @query('.suggestion') suggestionWrapper: HTMLDivElement;

  abortController = new AbortController();
  private readonly _name = 'autocomplete';
  private _searchText = '';
  private _searchResult: IAutocompleteResult;

  connectedCallback() {
    super.connectedCallback();
    this.isGeoSupported = this.locationservice.length > 0;
    this.addEventListener('keydown', this.onSearchBoxInputKeyDownEvent);
    this.addEventListener('blur', this.closeSuggestWrapper);
    this.adaptSearchboxPlaceholderDependingOnViewport();
  }

  protected firstUpdated(): void {
    PubSub.subscribe('window:resize', () => {
      this.closeSuggestWrapper();
      this.adaptSearchboxPlaceholderDependingOnViewport();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.onSearchBoxInputKeyDownEvent);
    this.removeEventListener('blur', this.closeSuggestWrapper);
  }

  render() {
    return html`${this.for == 'weather' ? this.getWeatherTemplate() : this.getSementTemplate()}`;
  }

  getWeatherTemplate() {
    return html`
      <div class="search-block">
        <div
          class="position-finder ${this.playPositionAnimation ? 'animation' : null} ${this.isGeoSupported ? null : 'hidden'}"
          @click=${this.handleGeoButtonClickEvent} title="Aktuelle Standort ermitteln">
          <span class="icon icon-position"></span>
          <span class="icon icon-spin"></span>
        </div>
        <div class="searchbox ${this.show ? 'suggest-open' : null}">
          <form action="${this.formAction}" method="get">
            <div>
              <input type="hidden" name="country" value="DE"/>
              <input class="searchterm" .value=${live(this.searchTerm)} @input=${this.handleInputChangeEvent}
                     type="text" name="term"
                     placeholder="${this.placeholder}" autocomplete="off"/>
              ${this.suggestions ? this.getSuggestionListTemplate() : null}
            </div>
            <button type="submit" class="primary">Suchen</button>
          </form>
        </div>
      </div>
      <div class="autocomplete-error ${this.hasGeoError ? 'show' : null}">Ihr Ort kann nicht ermittelt werden</div>
    `;
  }

  getSementTemplate() {
    return html`
      <h4 class="h4">Suche nach Thema</h4>
      <div class="search-block">
        <div class="searchbox ${this.show ? 'suggest-open' : null}">
          <form action="" method="get" @submit="${this.handleSubmitEvent}">
            <div>
              <input class="searchterm" .value=${live(this.searchTerm)} @input=${this.handleInputChangeEvent}
                     type="text" name="term"
                     placeholder="${this.placeholder}" autocomplete="off"/>
              ${this.suggestions ? this.getSuggestionListTemplate() : null}
            </div>
            <button type="submit" class="primary">Suchen</button>
          </form>
        </div>
      </div>
      ${this.showSearchResult ? this.getShowSearchResultTemplate() : null}
    `;
  }

  getShowSearchResultTemplate() {
    if (this.searchResultData) {
      if (this.searchResultData.suggestions.length) {
        return html`
          <ul class="sement-topics">
            ${this.searchResultData.suggestions.map((suggestion: AutocompleteSuggest) => html`
            <li><a href="${suggestion.url}" title="${suggestion.label}">${suggestion.label}</a></li>`)}
          </ul>`;
      }
      return html`
        <div> Es wurde leider kein Thema für den Begriff „${this.searchResultData.term}“ gefunden</div>`;
    }
    return html`
      <div> Es wurden leider keine Themen für den Begriff "" gefunden.</div>`;
  }

  getSuggestionListTemplate() {
    return html`
      <div class="suggestion">
        <ul>
          ${this.suggestions.map((suggestion, index) => html`
          <li class="autocomplete__suggestion ${this.selectedMenuIndex == index ? 'active' : null}">
            <a href="${suggestion.url}">${this.markInput(suggestion.label)}</a>
          </li>`)}
        </ul>
      </div>`;
  }

  private adaptSearchboxPlaceholderDependingOnViewport() {
    try {
      if (viewport.matches('tablet')) {
        this.placeholder = this.for == 'weather' ? 'Ort eingeben, z.B. Berlin, Wien, Zürich' : 'Thema eingeben z.B. Person';
      } else {
        this.placeholder = this.for == 'weather' ? 'Ort eingeben' : 'Thema eingeben';
      }
    } catch (e) {
      Logger.logUnknownError(e, this._name, true);
    }
  }

  /**
   * Prevent submitting the form and display search results instead.
   * @param {Event} e Submit event
   */
  handleSubmitEvent(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    this.searchResultData = this._searchResult;
    this.showSearchResult = true;
    this.closeSuggestWrapper();
  }

  onSearchBoxInputKeyDownEvent(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowUp':
        this.handleArrowUpEvent();
        break;
      case 'ArrowDown':
        this.handleArrowDownEvent();
        break;
      case 'Enter':
        this.handleEnterEvent();
        break;
      case 'Escape':
        this.closeSuggestWrapper();
        break;
    }
  }

  handleArrowUpEvent() {
    if (!this.show || this.selectedMenuIndex < 0) {
      return;
    }

    this.selectedMenuIndex--;
    this.scrollToView();
  }

  handleArrowDownEvent() {
    if (this.searchTerm === '') {
      return;
    }

    if (!this.show) {
      this.show = true;
    } else if (this.selectedMenuIndex < this.suggestions.length - 1) {
      this.selectedMenuIndex++;
      this.scrollToView();
    }
  }

  handleEnterEvent() {
    if (!this.show || this.selectedMenuIndex < 0) {
      return;
    }

    const activeElement = this.suggestions[this.selectedMenuIndex];

    if (activeElement !== undefined) {
      location.href = activeElement.url;
    }
  }

  handleInputChangeEvent(e: Event) {
    this.searchTerm = (e.target as HTMLInputElement).value.replace(/^\s+/, ''); // trim left
    const getURLChar = this.for == 'weather' ? '&' : '?';

    void (async () => {
      try {
        if (this.searchTerm.length > 0 && this.searchTerm !== this._searchText) {
          this._searchText = this.searchTerm;
          const searchURL = this.autoSuggestUrl + getURLChar + 'term=' + this.searchTerm;

          this.abortController.abort(); // abort the previous search
          this.abortController = new AbortController();

          const request = await fetch(searchURL, {signal: this.abortController.signal});
          const response = await request.json() as IAutocompleteResult;
          // reset suggest item highlight index
          this._searchResult = response;
          this.selectedMenuIndex = -1;

          // If response contains suggestions, and a string with at least three characters was searched, add suggestList to DOM
          if (this.searchTerm.length > 2 && response.suggestions.length > 0) {
            this.show = true;
            this.suggestions = response.suggestions;
          } else {
            this.closeSuggestWrapper();
          }
        } else if (this.searchTerm !== '' && this.searchTerm === this._searchText) {
          this.show = true;
        } else {
          this.closeSuggestWrapper();
        }
      } catch (e) {
        Logger.logUnknownError(e, this._name, true);
      }
    })();
  }

  handleGeoButtonClickEvent() {
    if (Features.geolocation()) {
      this.playPositionAnimation = true;
      this.getGeoLocation(this.locationservice);
    } else {
      this.isGeoSupported = false;
    }
  }

  async processAndSavePositionData(url: string, latitude: number, longitude: number) {
    const ajaxurl = url + '/' + latitude + '/' + longitude + '/';

    const geoRequest = await fetch(ajaxurl, {});
    const geoResponse = await geoRequest.json() as IGeoResponse;

    if (geoResponse.valid) {
      try {
        if (geoResponse.cities[0].loc_weather) { // CADNPCA-6262
          Cookies.setItem('loc_weather', geoResponse.cities[0].loc_weather, 60 * 60 * 24 * 365, '/', '.' + Url.getBaseDomain());
        } else {
          Cookies.setItem('weather_districtId', geoResponse.cities[0].districtId.toString(), 60 * 60 * 24 * 365, '/');
          Cookies.setItem('weather_countryCode', geoResponse.cities[0].countryCode, 60 * 60 * 24 * 365, '/');
        }
      } catch (e) {
        Logger.logUnknownError(e, this._name);
      }
      window.location.href = geoResponse.cities[0].url;
    } else {
      Logger.log(this._name, 'geolocation not valid', true);
      this.playPositionAnimation = false;
      this.hasGeoError = true;
    }
  }

  /**
   * Get the user's current geolocation.
   * Note that navigator.geolocation.getCurrentPosition() does not work on http connections any more in Chrome.
   * @param {string} url  The initial locationServiceUrl
   */
  getGeoLocation(url: string) {
    const currentPositionSuccessCallback = (position: GeolocationPosition) => {
      void this.processAndSavePositionData(url, position.coords.latitude, position.coords.longitude);
    };

    const currentPositionErrorCallback = (e: GeolocationPositionError): boolean => {
      Logger.log(this._name, 'getCurrentPosition error', true);
      Logger.log(this._name, e.message, true);
      this.playPositionAnimation = false;
      this.hasGeoError = true;
      return false;
    };

    navigator.geolocation.getCurrentPosition(currentPositionSuccessCallback, currentPositionErrorCallback);
  }

  closeSuggestWrapper() {
    this.selectedMenuIndex = -1;
    this.show = false;
  }

  escapeRegExp(string: string) {
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
    return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'); // $& means the whole matched string
  }

  markInput(suggestText: string) {
    const searchTerm = this.searchTerm.trim();
    const suggestParts = suggestText.split(new RegExp(this.escapeRegExp(searchTerm), 'ig'));
    const mark = html`<span class="autocomplete__highlight">${searchTerm}</span>`;
    const retVal: TemplateResult[] = [];
    suggestParts.forEach((s, i) => {
      retVal.push(html`${s}`);
      if (i < suggestParts.length - 1) {
        retVal.push(mark);
      }
    });
    return retVal;
  }

  scrollToView() {
    const suggestUL = this.suggestionWrapper.querySelector('ul');
    if (suggestUL && this.selectedMenuIndex > -1) {
      const activeLI = [...suggestUL.querySelectorAll('li')][this.selectedMenuIndex];
      suggestUL.scrollTop = activeLI.offsetTop - 9;
    } else if (suggestUL) {
      suggestUL.scrollTop = 0;
    }
  }
}
